/**
 * GrowthPage.tsx — 1% 성장 시각화
 *
 * 핵심 메시지:
 *  "오늘 딱 1개만 해도 돼. 그게 쌓이면 달라져."
 *
 * 구성:
 *  1. 복리 성장 시각화 (매일 1% 성장 vs 그냥 멈춤)
 *  2. 나의 실제 학습 성장 그래프 (progressStore 기반)
 *  3. 오늘의 1개 목표 체크 (완료 시 축하 애니메이션)
 *  4. 성장 달력 (연속 학습일 히트맵)
 */

import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, TrendingUp, CheckCircle, Circle,
  Flame, Star, Calendar, ChevronRight, Zap, Target
} from 'lucide-react'
import { useProgressStore, useStudyStreak, usePlayerLevel } from '../store/progressStore'
import { getCurrentUnit } from '../data/syllabus'

// ── 복리 계산 ─────────────────────────────────────────────────────
function compoundGrowth(rate: number, days: number): number {
  return Math.pow(1 + rate, days)
}

// ── 복리 그래프 SVG ──────────────────────────────────────────────
function CompoundGraph({ days }: { days: number }) {
  const W = 320, H = 180, PAD = { top: 16, right: 16, bottom: 28, left: 44 }
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const maxDays = 365
  const maxY = compoundGrowth(0.01, maxDays)

  const toSvg = (d: number, v: number) => ({
    x: PAD.left + (d / maxDays) * innerW,
    y: PAD.top + innerH - (v / maxY) * innerH,
  })

  // 1% 성장 선
  const growPath = Array.from({ length: maxDays + 1 }, (_, i) => {
    const { x, y } = toSvg(i, compoundGrowth(0.01, i))
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  // 0% (정체) 선
  const flatPath = `M${PAD.left},${toSvg(0, 1).y.toFixed(1)} L${PAD.left + innerW},${toSvg(maxDays, 1).y.toFixed(1)}`

  // 현재 위치 점
  const cur = toSvg(days, compoundGrowth(0.01, days))
  const curVal = compoundGrowth(0.01, days)

  // y축 눈금
  const yTicks = [1, 5, 10, 20, 37].filter(v => v <= maxY)

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
      <defs>
        <linearGradient id="growGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>

      {/* 그리드 */}
      {yTicks.map(v => {
        const sy = toSvg(0, v).y
        return (
          <g key={v}>
            <line x1={PAD.left} y1={sy} x2={W - PAD.right} y2={sy}
              stroke="#e2e8f0" strokeWidth={1} />
            <text x={PAD.left - 4} y={sy + 3} fontSize={8} fill="#94a3b8" textAnchor="end">
              {v}배
            </text>
          </g>
        )
      })}

      {/* x축 눈금 */}
      {[0, 90, 180, 270, 365].map(d => {
        const sx = PAD.left + (d / maxDays) * innerW
        return (
          <g key={d}>
            <line x1={sx} y1={PAD.top + innerH} x2={sx} y2={PAD.top + innerH + 4}
              stroke="#cbd5e1" strokeWidth={1} />
            <text x={sx} y={H - 4} fontSize={8} fill="#94a3b8" textAnchor="middle">
              {d === 0 ? '시작' : d === 365 ? '1년' : `${d}일`}
            </text>
          </g>
        )
      })}

      {/* 축 */}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + innerH}
        stroke="#94a3b8" strokeWidth={1.5} />
      <line x1={PAD.left} y1={PAD.top + innerH} x2={W - PAD.right} y2={PAD.top + innerH}
        stroke="#94a3b8" strokeWidth={1.5} />

      {/* 정체선 */}
      <path d={flatPath} fill="none" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5,3" />
      <text x={W - PAD.right - 2} y={toSvg(maxDays, 1).y - 4}
        fontSize={8} fill="#94a3b8" textAnchor="end">그냥 멈추면 1배</text>

      {/* 성장 면적 */}
      <path
        d={`${growPath} L${PAD.left + innerW},${PAD.top + innerH} L${PAD.left},${PAD.top + innerH} Z`}
        fill="url(#growGrad)"
      />

      {/* 성장선 */}
      <path d={growPath} fill="none" stroke="url(#lineGrad)" strokeWidth={2.5} strokeLinecap="round" />

      {/* 현재 위치 */}
      {days > 0 && (
        <g>
          <line x1={cur.x} y1={PAD.top} x2={cur.x} y2={PAD.top + innerH}
            stroke="#6366f1" strokeWidth={1} strokeDasharray="3,2" />
          <circle cx={cur.x} cy={cur.y} r={5} fill="#059669" stroke="white" strokeWidth={2} />
          {curVal > 1.05 && (
            <text x={cur.x + 6} y={cur.y - 4} fontSize={9} fill="#059669" fontWeight="bold">
              {curVal.toFixed(1)}배
            </text>
          )}
        </g>
      )}
    </svg>
  )
}

// ── 성장 달력 (히트맵) ───────────────────────────────────────────
function GrowthCalendar({ studyDates }: { studyDates: string[] }) {
  const today = new Date()
  const dateSet = new Set(studyDates)

  // 최근 5주 (35일)
  const days: { date: string; isToday: boolean; studied: boolean; dayOfWeek: number }[] = []
  for (let i = 34; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const dateStr = d.toISOString().slice(0, 10)
    days.push({
      date: dateStr,
      isToday: i === 0,
      studied: dateSet.has(dateStr),
      dayOfWeek: d.getDay(),
    })
  }

  const weeks: typeof days[] = []
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7))
  const dayLabels = ['일', '월', '화', '수', '목', '금', '토']

  return (
    <div className="space-y-2">
      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 gap-1.5 text-center">
        {dayLabels.map(d => (
          <div key={d} className="text-xs text-slate-400 font-medium">{d}</div>
        ))}
      </div>
      {/* 날짜 그리드 */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-1.5">
          {week.map((day, di) => (
            <div
              key={di}
              title={day.date}
              className={`
                aspect-square rounded-lg flex items-center justify-center text-[10px] font-bold
                transition-all
                ${day.isToday
                  ? 'ring-2 ring-indigo-400 ring-offset-1'
                  : ''}
                ${day.studied
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-400'}
              `}
            >
              {day.studied ? '✓' : new Date(day.date + 'T00:00:00').getDate()}
            </div>
          ))}
        </div>
      ))}
      <div className="flex items-center gap-2 justify-end mt-1">
        <div className="w-3 h-3 rounded bg-slate-100" />
        <span className="text-xs text-slate-400">미학습</span>
        <div className="w-3 h-3 rounded bg-emerald-500" />
        <span className="text-xs text-slate-400">학습 완료</span>
      </div>
    </div>
  )
}

// ── 현재 단원 링크 헬퍼 ─────────────────────────────────────────
function getCurrentPath(): string {
  const u = getCurrentUnit()
  if (!u) return '/syllabus'
  return u.contentPath ?? `/unit/${u.id}`
}
function getCurrentLabel(): string {
  const u = getCurrentUnit()
  return u ? `${u.id} · ${u.title}` : '현재 학습 단원'
}

// ── 오늘의 1개 목표 (링크는 렌더 시 getCurrentPath() 호출) ──────
function makeTodayTasks() {
  const path = getCurrentPath()
  return [
    { id: 'step',     emoji: '📖', label: '개념 1단계 완료하기',  xp: 50, link: path },
    { id: 'practice', emoji: '✏️', label: '연습문제 1개 풀기',    xp: 20, link: path },
    { id: 'problem',  emoji: '🧩', label: '문제풀이 1문제 도전',  xp: 30, link: path },
    { id: 'calc',     emoji: '🧮', label: '실생활 계산기 체험',   xp: 10, link: '/real-calc' },
  ]
}

// ── 메인 페이지 ─────────────────────────────────────────────────
export default function GrowthPage() {
  const streak = useStudyStreak()
  const player = usePlayerLevel()
  const data = useProgressStore(s => s.data)

  // 현재 단원 기반 동적 데이터
  const TODAY_TASKS = makeTodayTasks()
  const currentPath = getCurrentPath()
  const currentLabel = getCurrentLabel()

  const [checkedTasks, setCheckedTasks] = useState<Set<string>>(() => {
    try {
      const key = `growth_tasks_${new Date().toISOString().slice(0, 10)}`
      return new Set(JSON.parse(localStorage.getItem(key) ?? '[]'))
    } catch { return new Set() }
  })
  const [celebrated, setCelebrated] = useState(false)

  const toggleTask = (id: string) => {
    setCheckedTasks(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      try {
        const key = `growth_tasks_${new Date().toISOString().slice(0, 10)}`
        localStorage.setItem(key, JSON.stringify([...next]))
      } catch { /* noop */ }
      if (!celebrated && next.size >= 1) setCelebrated(true)
      return next
    })
  }

  // 실제 성장 데이터 계산
  const studyDates = streak.studyDates ?? []
  const totalStudyDays = studyDates.length
  const theoreticalGrowth = compoundGrowth(0.01, totalStudyDays)

  // 학습 완료 단계 수 (XP 기반 성장 포인트)
  const totalStepsCompleted = useMemo(() => {
    return Object.values(data.units).reduce((sum, u) => sum + u.learnCompletedCount, 0)
  }, [data])

  // 복리 시뮬레이션 일수 슬라이더
  const [simDays, setSimDays] = useState(100)
  const simVal = compoundGrowth(0.01, simDays)

  const allChecked = checkedTasks.size >= TODAY_TASKS.length

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">

      {/* 헤더 */}
      <div>
        <Link to="/" className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700 mb-4">
          <ArrowLeft size={15} /> 대시보드로
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-2xl shadow-md">
            📈
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800">1% 성장 시각화</h1>
            <p className="text-sm text-slate-500">매일 1%씩 성장하면 1년 후 37배가 돼요</p>
          </div>
        </div>
      </div>

      {/* 핵심 메시지 카드 */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-6 shadow-lg">
        <p className="text-xs font-bold text-emerald-200 mb-2 tracking-wide uppercase">오늘의 철학</p>
        <p className="text-2xl font-black leading-tight mb-3">
          "오늘 딱 1개만 해도 돼.<br />
          <span className="text-emerald-200">그게 쌓이면 달라져."</span>
        </p>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-3xl font-black">1%</p>
            <p className="text-emerald-200 text-xs">하루 성장률</p>
          </div>
          <div className="text-emerald-300 text-xl font-black">×365일 =</div>
          <div className="text-center">
            <p className="text-3xl font-black text-yellow-300">37배</p>
            <p className="text-emerald-200 text-xs">1년 후 성장</p>
          </div>
          <div className="text-emerald-300 text-xl font-black">vs</div>
          <div className="text-center">
            <p className="text-3xl font-black text-slate-300">1배</p>
            <p className="text-emerald-200 text-xs">그냥 멈추면</p>
          </div>
        </div>
      </div>

      {/* 복리 성장 시뮬레이터 */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-emerald-500" />
          <p className="font-black text-slate-700">복리 성장 시뮬레이터</p>
        </div>

        <CompoundGraph days={simDays} />

        {/* 슬라이더 */}
        <div className="mt-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-slate-600 font-medium">
              <span className="text-indigo-600 font-black">{simDays}일</span> 동안 매일 1%씩 성장하면…
            </span>
            <span className="text-lg font-black text-emerald-600">
              {simVal.toFixed(1)}배
            </span>
          </div>
          <input
            type="range" min={1} max={365} step={1} value={simDays}
            onChange={e => setSimDays(Number(e.target.value))}
            className="w-full" style={{ accentColor: '#10b981' }}
          />
          <div className="flex justify-between text-xs text-slate-400 mt-0.5">
            <span>1일</span><span>1년(365일)</span>
          </div>
        </div>

        {/* 비교 카드 */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
            <p className="text-xs text-emerald-600 font-bold mb-0.5">매일 1% 성장</p>
            <p className="text-2xl font-black text-emerald-700">{simVal.toFixed(1)}배</p>
            <p className="text-xs text-emerald-500 mt-0.5">{simDays}일 후</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-center">
            <p className="text-xs text-slate-500 font-bold mb-0.5">그냥 멈추면</p>
            <p className="text-2xl font-black text-slate-400">1배</p>
            <p className="text-xs text-slate-400 mt-0.5">변화 없음</p>
          </div>
        </div>
      </div>

      {/* 나의 실제 성장 현황 */}
      <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={16} className="text-indigo-500" />
          <p className="font-black text-indigo-700">나의 실제 성장</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              emoji: '📅',
              label: '학습한 날',
              value: `${totalStudyDays}일`,
              sub: `연속 ${streak.currentStreak}일`,
              color: 'text-indigo-700',
            },
            {
              emoji: '📖',
              label: '완료한 개념',
              value: `${totalStepsCompleted}개`,
              sub: `전체 12개 중`,
              color: 'text-purple-700',
            },
            {
              emoji: '⚡',
              label: '누적 XP',
              value: `${player.totalXp} XP`,
              sub: `Lv.${player.level} ${player.emoji}`,
              color: 'text-emerald-700',
            },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-3 text-center shadow-sm">
              <div className="text-xl mb-1">{item.emoji}</div>
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
              <p className="text-xs text-slate-400">{item.sub}</p>
            </div>
          ))}
        </div>

        {/* 이론적 성장배율 */}
        {totalStudyDays > 0 && (
          <div className="mt-3 bg-white rounded-xl p-3 flex items-center gap-3 shadow-sm">
            <div className="text-3xl">🌱</div>
            <div className="flex-1">
              <p className="text-sm font-black text-slate-700">
                {totalStudyDays}일 학습 = 이론상 <span className="text-emerald-600">{theoreticalGrowth.toFixed(2)}배</span> 성장!
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                1%씩 복리 계산 기준 · 꾸준히 하면 눈에 보여요
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 성장 달력 */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={16} className="text-slate-500" />
          <p className="font-black text-slate-700">나의 학습 달력</p>
          <span className="ml-auto text-xs text-slate-400">최근 5주</span>
        </div>
        {studyDates.length > 0
          ? <GrowthCalendar studyDates={studyDates} />
          : (
            <div className="text-center py-6 text-slate-400">
              <div className="text-4xl mb-2">📅</div>
              <p className="text-sm font-bold">아직 학습 기록이 없어요</p>
              <p className="text-xs mt-1">오늘 첫 학습을 시작하면 여기에 표시돼요!</p>
            </div>
          )
        }
      </div>

      {/* 오늘의 1개 목표 */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Target size={16} className="text-orange-500" />
          <p className="font-black text-slate-700">오늘의 1개 목표</p>
          <span className="ml-auto text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-bold">
            {checkedTasks.size} / {TODAY_TASKS.length} 완료
          </span>
        </div>
        <p className="text-xs text-slate-400 mb-4">딱 1개만 해도 오늘은 성공이에요!</p>

        <div className="space-y-2">
          {TODAY_TASKS.map(task => {
            const done = checkedTasks.has(task.id)
            return (
              <div
                key={task.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer
                  ${done
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-slate-50 border-slate-100 hover:border-slate-200'
                  }`}
                onClick={() => toggleTask(task.id)}
              >
                {done
                  ? <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                  : <Circle size={20} className="text-slate-300 flex-shrink-0" />
                }
                <span className="text-xl">{task.emoji}</span>
                <div className="flex-1">
                  <p className={`text-sm font-bold ${done ? 'text-emerald-700 line-through' : 'text-slate-700'}`}>
                    {task.label}
                  </p>
                  <p className="text-xs text-slate-400">+{task.xp} XP 획득</p>
                </div>
                <Link
                  to={task.link}
                  onClick={e => e.stopPropagation()}
                  className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5"
                >
                  이동 <ChevronRight size={12} />
                </Link>
              </div>
            )
          })}
        </div>

        {/* 완료 축하 */}
        {celebrated && (
          <div className="mt-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-4 text-center">
            <p className="text-2xl mb-1">🎉</p>
            <p className="font-black text-base">오늘의 1%를 달성했어요!</p>
            <p className="text-emerald-100 text-xs mt-0.5">
              이 작은 습관이 {streak.currentStreak + 1}일째 이어지고 있어요
            </p>
          </div>
        )}
      </div>

      {/* 동기부여 문장 */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 text-white p-6">
        <div className="space-y-4">
          {[
            { quote: '"1년 후의 나는, 오늘 시작한 덕분에 달라져 있을 것이다."', from: '성장의 법칙' },
            { quote: '"천천히 가도 괜찮아. 멈추지만 않으면 돼."', from: '수포자 탈출 선배의 말' },
            { quote: '"기울기가 양수면 언젠가 반드시 올라가. 수학이 그걸 증명해."', from: '일차함수의 진리' },
          ].map((q, i) => (
            <div key={i} className={i > 0 ? 'border-t border-white/10 pt-4' : ''}>
              <p className="text-sm text-white/90 leading-relaxed italic">
                {q.quote}
              </p>
              <p className="text-xs text-white/40 mt-1 text-right">— {q.from}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 학습 시작 CTA */}
      <Link
        to={currentPath}
        className="flex items-center justify-between w-full rounded-2xl p-5 text-white shadow-lg hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(135deg, #10b981, #0ea5e9)' }}
      >
        <div>
          <p className="font-black text-base">오늘의 1%를 시작하러 가기 🚀</p>
          <p className="text-white/70 text-sm mt-0.5">{currentLabel} — 개념 학습 → XP 획득 → 레벨업</p>
        </div>
        <ChevronRight size={22} className="text-white/80 flex-shrink-0" />
      </Link>

    </div>
  )
}
